// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {CompactCommentButton} from '@github-ui/commenting/CompactCommentButton'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {IDS} from '@github-ui/commenting/DomElements'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {useAnalytics} from '@github-ui/use-analytics'
import {Banner} from '@primer/react'
import {useRef, useState} from 'react'
import {useCreateReplyCommentMutation} from '@github-ui/pull-requests/use-create-reply-comment-mutation'

import {anchorComment} from '../../helpers/anchor-helpers'
import {generateSuggestedChangeLineRangeFromDiffThread} from '../../helpers/suggested-changes'
import {type Comment, type CommentAuthor, CommentReviewVariantType, type Thread} from '../../conversation-types'
import type {AddCommentEditorProps} from './AddCommentEditor'
import {AddCommentEditor} from './AddCommentEditor'
import {AutomatedReviewThreadComment, AutomatedReviewThreadCommentErrorFallback} from './AutomatedReviewThreadComment'
import styles from './ReviewThread.module.css'
import {ReviewThreadComment, type ReviewThreadCommentProps} from './ReviewThreadComment'
import type {DiffRange, FileKey, LineKey} from '../../types'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {usePullRequestBasePath} from '@github-ui/pull-requests/pull-request-store-selectors'
import {useIsFixWithCopilotAvailable} from '@github-ui/pull-requests/layout-selectors'

export interface ReviewThreadProps
  extends
    Pick<
      ReviewThreadCommentProps,
      'disableDialogMode' | 'enableDialogMode' | 'gridCellId' | 'isInDialogMode' | 'viewerData'
    >,
    Pick<AddCommentEditorProps, 'batchingEnabled' | 'suggestedChangesConfig'> {
  commentAnchorPrefix?: string
  getReactionContext?: (comment: Comment) => string
  disableSuggestedChangesActions?: boolean
  filePath: string
  isInlineComment: boolean
  modelsUrl?: string
  onDeleteLineComment?: () => void
  thread: Thread
  lineMarkersKey: LineKey | FileKey
  selectedRange?: DiffRange
  threadPositionNumber?: number
  threadsConnectionId?: string
  shouldLimitHeight?: boolean
  ghostUser?: CommentAuthor
  startExpanded?: boolean
  /** Disables the ability for the viewer to reply to this thread without needing to manage the viewerCanReply state */
  repliesDisabled?: boolean
  /** Overrides the internal quote-reply behavior. When set, the built-in composer is bypassed and the caller receives the quoted markdown to route into an external composer. */
  onQuoteReply?: (quotedText: string | undefined) => void
}

/**
 * Renders a given review thread's comments along with a reply comment box.
 *
 * TODO:
 * - Support paginated loading of large review threads.
 * - Better support for deleted comment authors.
 */
export function ReviewThread({
  getReactionContext,
  batchingEnabled,
  commentAnchorPrefix,
  lineMarkersKey,
  disableDialogMode,
  disableSuggestedChangesActions = false,
  enableDialogMode,
  filePath,
  gridCellId,
  isInlineComment,
  isInDialogMode,
  modelsUrl,
  onDeleteLineComment,
  selectedRange,
  thread,
  threadPositionNumber,
  shouldLimitHeight = true,
  suggestedChangesConfig,
  viewerData,
  ghostUser,
  startExpanded = false,
  repliesDisabled = false,
  onQuoteReply: externalOnQuoteReply,
}: ReviewThreadProps) {
  const [isReplying, setIsReplying] = useState(startExpanded)
  const [quoteReplyText, setQuoteReplyText] = useState<string | undefined>(undefined)
  const {sendAnalyticsEvent} = useAnalytics()
  const basePath = usePullRequestBasePath()
  const isFixWithCopilotAvailable = useIsFixWithCopilotAvailable()
  const viewerCanReply = thread.viewerCanReply && !repliesDisabled

  const replyButtonRef = useRef<HTMLButtonElement>(null)
  const replyContainerRef = useRef<HTMLDivElement>(null)

  const {mutate: addReplyMutation} = useCreateReplyCommentMutation(filePath, lineMarkersKey)

  const addReplyComment = ({
    commentText,
    onCompleted,
    onError,
    submitBatch,
  }: {
    commentText: string
    onCompleted?: (commentDatabaseId?: number) => void
    onError: (error: Error) => void
    submitBatch?: boolean
  }) => {
    const handleCompleted = (commentDatabaseId: number | null | undefined) => {
      if (commentDatabaseId) anchorComment(commentDatabaseId.toString())
      onCompleted?.()
    }

    const lastComment = thread.commentsData?.comments[thread.commentsData?.comments.length - 1]
    const inReplyTo = lastComment?.databaseId
    addReplyMutation(
      {
        text: commentText,
        submitBatch,
        inReplyTo,
        path: filePath,
        comparisonEndOid: selectedRange?.headOid,
        comparisonStartOid: selectedRange?.baseOid,
      },
      {
        onError,
        onSuccess: data => handleCompleted(data.comment.databaseId),
      },
    )

    sendAnalyticsEvent('comments.add', 'ADD_COMMENT_BUTTON')
  }

  const cancelComment = () => {
    setIsReplying(false)
    setQuoteReplyText(undefined)
    sendAnalyticsEvent('comments.cancel_thread_reply', 'CANCEL_REVIEW_THREAD_BUTTON')
    ssrSafeWindow?.requestAnimationFrame(() => replyButtonRef.current?.focus())
  }

  const onQuoteReply = (text?: string) => {
    if (externalOnQuoteReply) {
      externalOnQuoteReply(text)
      return
    }
    setQuoteReplyText(text)
    setIsReplying(true)
  }

  if (!thread.commentsData || thread.commentsData.comments === null || thread.commentsData.comments.length < 1)
    return null
  const lineRange = generateSuggestedChangeLineRangeFromDiffThread(thread)
  const applySuggestedChangesValidationData =
    !disableSuggestedChangesActions && thread.subjectType?.toLowerCase() === 'line'
      ? {
          lineRange,
        }
      : undefined

  const handleReplyContainerFocus = (e: React.FocusEvent) => {
    // Only handle focus logic if a user is on the reply container's first <div> element.
    if (replyContainerRef.current !== e.target) return

    if (isReplying) {
      // Focus on the textarea element inside of the reply container
      replyContainerRef.current?.querySelector('textarea')?.focus()
    } else {
      // Focus on the Write a reply button.
      replyButtonRef.current?.focus()
    }
  }

  const isAutomatedComment = (comment: (typeof thread.commentsData.comments)[0], index: number) => {
    return comment.reviewVariantType === CommentReviewVariantType.Automated && index === 0
  }

  return (
    <div data-testid="review-thread">
      <div className={styles.ReviewThreadContainer} data-limit-height={shouldLimitHeight}>
        {thread.commentsData.comments.map((comment, index) => {
          if (isAutomatedComment(comment, index)) {
            return (
              <ErrorBoundary
                key={comment.id}
                fallback={<AutomatedReviewThreadCommentErrorFallback />}
                boundaryName="AutomatedReviewThreadComment"
              >
                <AutomatedReviewThreadComment
                  reactionContext={getReactionContext?.(comment)}
                  anchorPrefix={commentAnchorPrefix}
                  basePath={basePath}
                  comment={comment}
                  isAnchorable
                  isFixWithCopilotAvailable={isFixWithCopilotAvailable}
                  isInDialogMode={isInDialogMode}
                  isOutdated={thread.positioning?.isOutdated}
                  isThreadResolved={!!thread.isResolved}
                  modelsUrl={modelsUrl}
                  onQuoteReply={onQuoteReply}
                  threadId={thread.id}
                  threadPositionNumber={threadPositionNumber}
                  footnoteTextHTML={comment.footnoteTextHTML}
                />
              </ErrorBoundary>
            )
          }
          return (
            <ReviewThreadComment
              reactionContext={getReactionContext?.(comment)}
              lineMarkersKey={lineMarkersKey}
              lineNumber={lineRange?.endLineNumber}
              startLineNumber={lineRange?.startLineNumber}
              comparisonRange={selectedRange}
              isAnchorable
              index={index}
              threadPositionNumber={threadPositionNumber}
              isFirstComment={index === 0}
              isInlineComment={isInlineComment}
              isLastChild={index === (thread.commentsData?.comments.length ?? 0) - 1}
              key={comment.id}
              anchorPrefix={commentAnchorPrefix}
              comment={comment}
              commentConnectionId={thread.commentsData?.__id}
              disableDialogMode={disableDialogMode}
              enableDialogMode={enableDialogMode}
              filePath={filePath}
              gridCellId={gridCellId}
              isInDialogMode={isInDialogMode}
              isOutdated={thread.positioning?.isOutdated}
              threadId={thread.id}
              onDeleteLineComment={onDeleteLineComment}
              onQuoteReply={onQuoteReply}
              threadCommentCount={thread.commentsData?.comments.length}
              isThreadResolved={!!thread.isResolved}
              suggestedChangesConfig={suggestedChangesConfig}
              applySuggestedChangesValidationData={applySuggestedChangesValidationData}
              viewerData={viewerData}
              ghostUser={ghostUser}
            />
          )
        })}
        {thread.reviewCommentsLimitExceeded && (
          <Banner
            aria-label="Warning"
            title="Warning"
            variant="warning"
            hideTitle
            description={`Only the first ${(thread.reviewCommentsLimit || 0) - 1} replies are currently being shown.`}
            className="tmp-m-3"
          />
        )}
        {viewerCanReply && (
          <div
            id={IDS.issueCommentComposer}
            className="rounded-bottom-2 p-2 bgColor-inset"
            data-marker-navigation-comment-thread-id={thread.id}
            data-marker-navigation-comment-id={`${thread.id}-reply-comment`}
            data-marker-navigation-thread-reply
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            tabIndex={isInDialogMode ? 0 : -1}
            onFocus={handleReplyContainerFocus}
            ref={replyContainerRef}
          >
            {isReplying && (
              <AddCommentEditor
                startLineNumber={lineRange?.startLineNumber}
                lineNumber={lineRange?.endLineNumber}
                batchingEnabled={batchingEnabled}
                condensed={false}
                fileLevelComment
                filePath={filePath}
                focusOnMount
                isReplying
                onCancelComment={cancelComment}
                quotedText={quoteReplyText}
                threadId={thread.id}
                onAddComment={addReplyComment}
                suggestedChangesConfig={suggestedChangesConfig}
              />
            )}
            {!isReplying && (
              <CompactCommentButton
                ref={replyButtonRef}
                onClick={() => {
                  sendAnalyticsEvent('comments.start_thread_reply', 'REPLY_TO_THREAD_INPUT_BUTTON')
                  setIsReplying(true)
                }}
              >
                Write a reply
              </CompactCommentButton>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
